import { DocumentData, QueryDocumentSnapshot, arrayRemove, arrayUnion, updateDoc } from "firebase/firestore";
import { callApi, RequestType } from "../fetch";
import { showToast } from "../toast";
import { refusedByRules } from "./person_settings";

// Like people's settings (person_settings.ts): once a company has switched over to the new booking service,
// the Firestore rules refuse OnePark's direct writes to its company document and to groups holding its people.
// OnePark writes directly as it always has and, only when the rules refuse, sends the same change through
// the service. Companies that haven't switched cost nothing extra.

// A company's or group's settings couldn't be saved, and the admin has already been told which. Callers let
// it propagate, so a rule save stops instead of reporting success.
export class SettingsSaveError extends Error {
    name = "SettingsSaveError";
}
// By name, not instanceof: transpiled builds can lose an Error subclass's prototype.
export const isSettingsSaveError = (error: unknown): boolean => (error as Error | null)?.name === "SettingsSaveError";

const refused = (what: string): never => {
    const message = `We couldn't update ${what} just now, so your change wasn't fully saved. Please try again.`;
    showToast(message, { type: "error" });
    throw new SettingsSaveError(message);
};

const groupLabel = (name: unknown): string => (typeof name === "string" && name.trim() ? `the ${name} group` : "this group");

type CompanyRules = { add?: string[]; remove?: string[] };

// Sets a rule's values on one company (its booking limit, weekend and multi-day switches and so on), and adds
// or removes the rule's name in the company's tenantRules.
export const saveCompanySettings = async (tenantDoc: QueryDocumentSnapshot<DocumentData>, changes: Record<string, unknown>,
    rules: CompanyRules = {}): Promise<void> => {
    const tenantRules = rules.add?.length ? { tenantRules: arrayUnion(...rules.add) } :
        rules.remove?.length ? { tenantRules: arrayRemove(...rules.remove) } : {};
    try {
        await updateDoc(tenantDoc.ref, { ...tenantDoc.data(), ...changes, ...tenantRules });
        return;
    } catch (error) {
        if (!refusedByRules(error)) throw error;
    }
    const response = await callApi("bookingApiV2/tenants/update", {
        tenantId: tenantDoc.id, set: changes,
        ...(rules.add?.length ? { addRules: rules.add } : {}), ...(rules.remove?.length ? { removeRules: rules.remove } : {}),
    }, RequestType.POST, { signedIn: true });
    if (response.error) refused(`${tenantDoc.data().name || "this company"}'s settings`);
};

// Sets a rule's values on one user group (its booking limit, future booking period and so on).
export const saveGroupSettings = async (groupDoc: QueryDocumentSnapshot<DocumentData>, changes: Record<string, unknown>): Promise<void> => {
    try {
        await updateDoc(groupDoc.ref, { ...groupDoc.data(), ...changes });
        return;
    } catch (error) {
        if (!refusedByRules(error)) throw error;
    }
    const response = await callApi("bookingApiV2/groups/update", { groupId: groupDoc.id, set: changes }, RequestType.POST, { signedIn: true });
    if (response.error) refused(`${groupLabel(groupDoc.data().name)}'s settings`);
};

// What a switched-over company's group may change through the service.
const GROUP_FIELDS = ["name", "description", "bookingLimit", "futureBookingPeriod", "lastMinuteBookingPeriod", "appliedRules"];

export const createGroupViaService = async (group: Record<string, unknown>): Promise<string> => {
    const response = await callApi<{ data?: { groupId?: string } }>("bookingApiV2/groups/create", { group }, RequestType.POST, { signedIn: true });
    const groupId = response.data?.data?.groupId;
    if (response.error || !groupId) return refused(groupLabel(group.name));
    return groupId;
};

// Sends only what changed: the allowed fields, and the people added and removed.
export const updateGroupViaService = async (groupId: string, before: Record<string, unknown>, after: Record<string, unknown>): Promise<void> => {
    const set = Object.fromEntries(GROUP_FIELDS.filter((key) => after[key] !== undefined &&
        JSON.stringify(after[key]) !== JSON.stringify(before[key])).map((key) => [key, after[key]]));
    const was = (before.users as string[] | undefined) ?? [];
    const now = (after.users as string[] | undefined) ?? [];
    const addUsers = now.filter((id) => !was.includes(id));
    const removeUsers = was.filter((id) => !now.includes(id));
    if (!Object.keys(set).length && !addUsers.length && !removeUsers.length) return;
    const response = await callApi("bookingApiV2/groups/update", {
        groupId, ...(Object.keys(set).length ? { set } : {}), ...(addUsers.length ? { addUsers } : {}), ...(removeUsers.length ? { removeUsers } : {}),
    }, RequestType.POST, { signedIn: true });
    if (response.error) refused(groupLabel(after.name));
};

export const deleteGroupViaService = async (groupId: string, name: string): Promise<void> => {
    const response = await callApi("bookingApiV2/groups/delete", { groupId }, RequestType.POST, { signedIn: true });
    if (response.error) refused(groupLabel(name));
};

// Adds or removes a group on a rule through the service, when the rules refuse the direct write.
export const linkGroupToRuleViaService = async (ruleId: string, groupId: string, link: boolean): Promise<void> => {
    const response = await callApi("bookingApiV2/rules/update", { ruleId, [link ? "add" : "remove"]: { userGroups: [groupId] } },
        RequestType.POST, { signedIn: true });
    if (response.error) refused("this group's rules");
};
